/**
 * Endpoints de Conciliacion. Cada funcion devuelve el payload tipado del body.
 *
 * Mapeo backend:
 *   GET  /reportes                           -> a2c-client-asiContadores/src/lambdas/conciliacion_get
 *   GET  /reportes/{rfc}                     -> conciliacion_get_detalle
 *   GET  /reportes/{rfc}/download            -> conciliacion_get_download
 *   POST /reportes/{rfc}/download-filtrado   -> conciliacion_get_download_filtrado_iniciar (crea job async)
 *   GET  /reportes/{rfc}/download-filtrado/status/{job_id} -> conciliacion_get_download_filtrado_status (polling)
 *   GET  /reportes/{rfc}/pdfs                -> conciliacion_get_pdfs
 *   POST /match                              -> conciliacion_match_manual
 *   DELETE /match/{id}                       -> conciliacion_match_manual (anular un match ya hecho)
 *   GET  /solicitudes/{rfc}                  -> listar_solicitudes (detalle por sub-ventana)
 */
import { apiClient } from './clienteHttp';
import type {
  ReporteListItem,
  ReporteDetalle,
  DetalleFiltros,
  PdfFile,
  MatchManualPayload,
  SolicitudPayload,
  SolicitudResult,
  VerificarIniciarResult,
  SolicitudEstado,
  SolicitudesPorRfcResult,
  ConciliarPayload,
  ConciliarResult,
  ConciliarJobStatus,
  DescargaFiltradaJobStatus,
  PresignedUrlsPayload,
  PresignedUrlsResult,
  ColaItem,
} from './types';

/** Traduce DetalleFiltros a query params HTTP, omitiendo valores vacíos —
 * el backend no conoce el sentinel 'TODOS' de los <Select> del frontend, un
 * filtro "sin aplicar" simplemente no debe mandar el query param. */
function filtrosAQueryParams(filtros?: DetalleFiltros): Record<string, string | number> | undefined {
  if (!filtros) return undefined;
  const params: Record<string, string | number> = {};
  if (filtros.id) params.id = filtros.id;
  if (filtros.tab) params.tab = filtros.tab;
  if (filtros.status && filtros.status !== 'TODOS') params.status = filtros.status;
  if (filtros.mes && filtros.mes !== 'TODOS') params.mes = filtros.mes;
  if (filtros.desde) params.desde = filtros.desde;
  if (filtros.hasta) params.hasta = filtros.hasta;
  if (filtros.banco && filtros.banco !== 'TODOS') params.banco = filtros.banco;
  if (filtros.numero_cuenta && filtros.numero_cuenta !== 'TODOS') params.numero_cuenta = filtros.numero_cuenta;
  if (filtros.moneda && filtros.moneda !== 'TODOS') params.moneda = filtros.moneda;
  if (filtros.tipo_doc) params.tipo_doc = filtros.tipo_doc;
  if (filtros.metodo_pago) params.metodo_pago = filtros.metodo_pago;
  if (filtros.uuid) params.uuid = filtros.uuid;
  if (filtros.rfc) params.rfc = filtros.rfc;
  if (filtros.monto_min != null) params.monto_min = filtros.monto_min;
  if (filtros.monto_max != null) params.monto_max = filtros.monto_max;
  if (filtros.page != null) params.page = filtros.page;
  if (filtros.page_size != null) params.page_size = filtros.page_size;
  return params;
}

export async function listReportes(): Promise<ReporteListItem[]> {
  const { data } = await apiClient.get<unknown>('/reportes');
  // El backend puede devolver:
  //   1) array directo                       (lambda conciliacion_get nuevo)
  //   2) { reportes: [...] }                 (lambda reportes legacy)
  //   3) { body: [...] }                     (legacy con envelope adicional)
  if (Array.isArray(data))                                   return data as ReporteListItem[];
  if (data && Array.isArray((data as { reportes?: unknown }).reportes))
    return (data as { reportes: ReporteListItem[] }).reportes;
  if (data && Array.isArray((data as { body?: unknown }).body))
    return (data as { body: ReporteListItem[] }).body;
  return [];
}

export async function getReporteDetalle(rfc: string, filtros?: DetalleFiltros): Promise<ReporteDetalle> {
  const { data } = await apiClient.get<unknown>(`/reportes/${encodeURIComponent(rfc)}`, {
    params: filtrosAQueryParams(filtros),
  });
  // El backend puede envolver la respuesta en `body` (legacy).
  if (data && typeof data === 'object' && 'rfc' in (data as object)) return data as ReporteDetalle;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: ReporteDetalle }).body;
  return data as ReporteDetalle;
}

export async function getReporteDownload(rfc: string): Promise<{ url: string; filename: string }> {
  const { data } = await apiClient.get<unknown>(`/reportes/${encodeURIComponent(rfc)}/download`);
  if (data && typeof data === 'object' && 'url' in (data as object))
    return data as { url: string; filename: string };
  if (data && typeof data === 'object' && (data as { body?: { url?: string } }).body?.url)
    return (data as { body: { url: string; filename: string } }).body;
  return data as { url: string; filename: string };
}

/** Excel filtrado — se genera en el backend (ver conciliacion_get_download_filtrado)
 * porque, con paginación real, el frontend ya no tiene el dataset completo
 * filtrado en memoria para armarlo client-side como antes.
 *
 * Es async (POST inicia un job + GET status hace polling) porque, con
 * columnas CFDI configurables (Editar Columnas) que requieren re-parsear
 * XML de S3, la generación puede superar los 29s que tolera una integración
 * síncrona de API Gateway REST — ver conciliacion_get_download_filtrado_iniciar. */
export async function iniciarReporteDownloadFiltrado(rfc: string, filtros?: DetalleFiltros): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string; estado: string }>(
    `/reportes/${encodeURIComponent(rfc)}/download-filtrado`,
    null,
    { params: filtrosAQueryParams(filtros) },
  );
  return data;
}

export async function getReporteDownloadFiltradoStatus(rfc: string, jobId: string): Promise<DescargaFiltradaJobStatus> {
  const { data } = await apiClient.get<DescargaFiltradaJobStatus>(
    `/reportes/${encodeURIComponent(rfc)}/download-filtrado/status/${encodeURIComponent(jobId)}`,
  );
  return data;
}

export async function getReportePdfs(rfc: string): Promise<{ pdfs: PdfFile[] }> {
  const { data } = await apiClient.get<unknown>(`/reportes/${encodeURIComponent(rfc)}/pdfs`);
  if (data && typeof data === 'object' && 'pdfs' in (data as object))
    return data as { pdfs: PdfFile[] };
  if (data && typeof data === 'object' && (data as { body?: { pdfs?: PdfFile[] } }).body?.pdfs)
    return (data as { body: { pdfs: PdfFile[] } }).body;
  return { pdfs: [] };
}

export async function postMatchManual(payload: MatchManualPayload): Promise<void> {
  await apiClient.post('/match', payload);
}

/** Anula un match ya hecho (automático o manual) — la fila vuelve a "No
 * conciliados" y el CFDI/movimiento quedan disponibles otra vez en
 * Conciliación Manual. `resultadoId` es `item.banco.id` (el id de la fila
 * `resultado_conciliacion`, ya expuesto hoy en cada `DetalleItem` MATCHED).
 * Sobre un traspaso entre cuentas (TC) deshace ambas patas. */
export async function anularMatch(rfc: string, resultadoId: string, motivo: string): Promise<void> {
  await apiClient.delete(`/match/${encodeURIComponent(resultadoId)}`, { data: { rfc, motivo } });
}

export async function deleteSolicitud(id: string): Promise<void> {
  await apiClient.delete(`/solicitud/${encodeURIComponent(id)}`);
}

export async function postSolicitar(payload: SolicitudPayload): Promise<SolicitudResult> {
  const { data } = await apiClient.post<SolicitudResult>('/solicitar', payload);
  return data;
}

export async function postVerificar(solicitudIds?: string[]): Promise<VerificarIniciarResult> {
  const { data } = await apiClient.post<VerificarIniciarResult>('/verificar',
    solicitudIds?.length ? { id_solicitudes: solicitudIds } : {}
  );
  return data;
}

/** Detalle por sub-solicitud (ventana × tipo) de un RFC — para mostrar cuáles
 * ventanas quedaron en error y permitir reintentarlas puntualmente. */
export async function getSolicitudesPorRfc(rfc: string): Promise<SolicitudesPorRfcResult> {
  const { data } = await apiClient.get<unknown>(`/solicitudes/${encodeURIComponent(rfc)}`);
  if (data && typeof data === 'object' && 'solicitudes' in (data as object)) return data as SolicitudesPorRfcResult;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: SolicitudesPorRfcResult }).body;
  return data as SolicitudesPorRfcResult;
}

export async function getSolicitudEstado(id: string): Promise<SolicitudEstado> {
  const { data } = await apiClient.get<unknown>(`/solicitud/${encodeURIComponent(id)}`);
  if (data && typeof data === 'object' && 'estado' in (data as object)) return data as SolicitudEstado;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: SolicitudEstado }).body;
  return data as SolicitudEstado;
}

export async function postPresignedUrls(payload: PresignedUrlsPayload): Promise<PresignedUrlsResult> {
  const { data } = await apiClient.post<unknown>('/presigned-urls', payload);
  if (data && typeof data === 'object' && 'archivos' in (data as object)) return data as PresignedUrlsResult;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: PresignedUrlsResult }).body;
  return data as PresignedUrlsResult;
}

export async function postConciliar(payload: ConciliarPayload): Promise<ConciliarResult> {
  const { data } = await apiClient.post<unknown>('/conciliar', payload);
  if (data && typeof data === 'object' && 'job_id' in (data as object)) return data as ConciliarResult;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: ConciliarResult }).body;
  return data as ConciliarResult;
}

export async function getConciliarStatus(jobId: string): Promise<ConciliarJobStatus> {
  const { data } = await apiClient.get<unknown>(`/conciliar/status/${encodeURIComponent(jobId)}`);
  if (data && typeof data === 'object' && 'job_id' in (data as object)) return data as ConciliarJobStatus;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: ConciliarJobStatus }).body;
  return data as ConciliarJobStatus;
}

export async function getCola(signal?: AbortSignal): Promise<ColaItem[]> {
  const { data } = await apiClient.get<unknown>('/cola', { signal });
  if (data && typeof data === 'object' && 'trabajos' in (data as object))
    return (data as { trabajos: ColaItem[] }).trabajos;
  if (data && typeof data === 'object' && 'body' in (data as object) && (data as { body: unknown }).body)
    return (data as { body: { trabajos: ColaItem[] } }).body.trabajos;
  return [];
}

export async function postCola(trabajos: ColaItem[], signal?: AbortSignal): Promise<void> {
  await apiClient.post('/cola', { trabajos }, { signal });
}
